import { Container, Grid, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { GridColDef } from '@mui/x-data-grid';
import { useContext, useEffect, useState } from 'react';

import CreateTask from 'components/Task/CreateTask';
import { ROUTE } from 'route/RouteEnums';
import { useAppDispatch } from 'store/hooks';
import { getTaskCount, getTaskList } from 'store/slices/secureMessageSlice';
import COLORS from 'theme/colors';
import { StyledDataGrid } from 'theme/datagrid';
import { AuthContext } from 'utils/AuthContext';
import { CustomToolbar } from 'utils/customToolBar';
import { formatTaskDueDate } from 'utils/dataFormatter';
import { DateFilter } from 'utils/gridOperators';

// Open tasks are listed under the tab of their category, closed tasks of every category under Closed.
// countKey matches the status field of the /task/count response.
const TASK_TABS = [
  { label: 'Chart Updates', category: 'CHART_UPDATES', countKey: 'CHART_UPDATES' },
  { label: 'Billing', category: 'BILLING', countKey: 'BILLING' },
  { label: 'Staff Created', category: 'STAFF_CREATED', countKey: 'STAFF_CREATED' },
  { label: 'Provider Reconciliation', category: 'PROVIDER_RECONCILIATION', countKey: 'PROVIDER_RECONCILIATION' },
  { label: 'Closed', listType: 'closed', countKey: 'closed' },
];
const CLOSED_TAB = TASK_TABS.length - 1;

const TASK_CATEGORY_LABELS: Record<string, string> = {
  STAFF_CREATED: 'Staff Created',
  PROVIDER_RECONCILIATION: 'Provider Reconciliation',
  CHART_UPDATES: 'Chart Updates',
  BILLING: 'Billing',
};

// Any value other than low/medium is shown as High, as before.
const TASK_PRIORITY_DISPLAY: Record<string, { label: string; color: string }> = {
  low: { label: 'Low', color: '#12B76A' },
  medium: { label: 'Medium', color: '#CA8504' },
  high: { label: 'High', color: '#F04438' },
};

const NoRowsOverlay = () => (
  <Typography variant='body2' sx={{ padding: 2, textAlign: 'center' }}>
    No records found
  </Typography>
);

const TaskList = () => {
  const [taskList, setTaskList] = useState<any[] | null>();
  const [editItem, setEditItem] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [taskCount, setTaskCount] = useState<Record<string, number>>({});
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);

  const getList = async () => {
    const tab = TASK_TABS[selectedTab];
    const data = {
      id: user?.userId,
      category: tab.category,
      listType: tab.listType,
    };

    await dispatch(getTaskList(data))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setTaskList(response?.data);
          setLoading(false);
        }
      });

    await dispatch(getTaskCount({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const counts: Record<string, number> = {};
          response?.data?.forEach((x: any) => {
            counts[x?.status] = x?.count;
          });
          setTaskCount(counts);
        }
      });
  };

  useEffect(() => {
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItem, selectedTab]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      flex: 0.5,
      filterable: false,
    },
    {
      field: 'taskTitle',
      headerName: 'Task Title',
      flex: 1.5,
    },
    {
      field: 'assignedBy',
      headerName: 'Assigned By',
      flex: 1,
    },
    {
      field: 'assignedTo',
      headerName: 'Assigned To',
      flex: 1,
    },
    {
      field: 'category',
      headerName: 'Category',
      flex: 1,
      valueGetter: (params: any) => TASK_CATEGORY_LABELS[params?.row?.category] ?? params?.row?.category,
    },
    {
      field: 'status',
      headerName: 'Task Status',
      flex: 1,
      renderCell: (params: any) => {
        return (
          <span
            style={{
              textTransform: 'capitalize',
            }}
          >
            {params?.row?.status}
          </span>
        );
      },
    },
    {
      field: 'dueDate',
      headerName: 'Due Date',
      flex: 1,
      type: 'date',
      filterOperators: DateFilter,
      valueFormatter: (params: any) => formatTaskDueDate(params?.row?.dueDate),
      valueGetter: (params: any) => formatTaskDueDate(params?.row?.dueDate),
      renderCell: (params: any) => {
        return <>{formatTaskDueDate(params?.row?.dueDate)}</>;
      },
    },
    {
      field: 'priority',
      headerName: 'Priority',
      flex: 1,
      renderCell: (params: any) => {
        const priority = TASK_PRIORITY_DISPLAY[params?.row?.priority] ?? TASK_PRIORITY_DISPLAY.high;
        return <span style={{ color: priority.color }}>{priority.label}</span>;
      },
    },
    {
      field: 'patientName',
      headerName: 'Patient Name',
      flex: 1,
      renderCell: (params: any) => {
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip title='Patient Chart'>
              <IconButton
                size='small'
                aria-label='Patient Chart'
                onClick={(event) => {
                  event.stopPropagation();
                  openPatientChart(params.row.patientId);
                }}
              >
                <PersonIcon fontSize='small' sx={{ color: '#3366FF' }}  />
              </IconButton>
            </Tooltip>
            <span
              style={{
                textTransform: 'capitalize',
              }}
            >
              {params?.row?.patientName}
            </span>
          </span>
        );
      },
    },
  ];

  const openPatientChart = (patientId: string | number) => {
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${patientId}`, '_blank');
  };

  const rowSelect = (params: any) => {
    // eslint-disable-next-line array-callback-return
    const editItem = taskList?.filter((d: any) => {
      if (d.id === params.row.id) {
        return d;
      }
    });
    setEditItem(editItem);
  };

  const handleTabChange = (event: any, newValue: any) => {
    setSelectedTab(newValue);
  };

  return (
    <Container maxWidth='lg'>
      <Grid item xs={12}>
        <Typography
          variant='h5'
          position={'relative'}
          width={'fit-content'}
          sx={{ paddingTop: 6, textTransform: 'capitalize' }}
        >
          Task List
        </Typography>
      </Grid>
      <Grid item xs={12} sx={{ backgroundColor: COLORS.White }}>
        <Grid container sx={{ background: '#FFFBF1' }}>
          <Grid item xs={12} mb='10px'>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant='scrollable'
              scrollButtons='auto'
              sx={{
                background: '#F9FAFB',
                borderRadius: '23px',
                border: '1px solid #F2F4F7',
                marginTop: '10px',
                padding: '6px 14px',
                '.MuiTab-root.Mui-selected': {
                  background: '#9F84FF',
                  color: '#fff',
                  borderRadius: '23px',
                  fontWeight: 500,
                },
              }}
              TabIndicatorProps={{
                style: {
                  background: 'transparent',
                },
              }}
            >
              {TASK_TABS.map((tab, index) => (
                <Tab
                  key={tab.countKey}
                  label={
                    <Grid>
                      {tab.label}
                      <span
                        style={{
                          background: selectedTab === index ? '#644EB5' : '#F2F4F7',
                          borderRadius: '23px',
                          padding: '5px 7px',
                          marginLeft: '5px',
                        }}
                      >
                        {taskCount[tab.countKey] ?? 0}
                      </span>
                    </Grid>
                  }
                />
              ))}
            </Tabs>
          </Grid>
        </Grid>

        <StyledDataGrid
          autoHeight
          rows={taskList ? taskList : []}
          columns={columns}
          loading={loading}
          onRowClick={rowSelect}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
            sorting: {
              sortModel: [{ field: 'id', sort: 'desc' }],
            },
          }}
          disableColumnMenu
          slots={{ toolbar: CustomToolbar, noRowsOverlay: NoRowsOverlay }}
          slotProps={{ toolbar: { col: columns, disableManageCol: false, disableFilterList: false } }}
          columnVisibilityModel={{
            id: false,
            category: selectedTab === CLOSED_TAB,
            assignedTo: user?.permissions.includes('CONVERSATIONS_INSIGHT') ? true : false,
          }}
          pageSizeOptions={[10, 25, 50]}
          rowSpacingType='margin'
          getRowClassName={(params: any) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
          style={{ cursor: 'pointer' }}
        />
      </Grid>
      {editItem !== null && (
        <CreateTask
          onClose={() => setEditItem(null)}
          editData={editItem !== null ? editItem : null}
          userId={editItem[0].mindwealUserId}
          patientName={editItem === null ? editItem[0].userName : null}
        />
      )}
    </Container>
  );
};
export default TaskList;
